import { useState } from 'react';
import { useOrgContext } from '@/contexts/orgContext';
import { trackGraphDeletedEvent } from '@/utils/analytics/trackEngineEvents';
import { useStyletron } from '@tigergraph/app-ui-lib/Theme';
import { useMutation } from 'react-query';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@tigergraph/app-ui-lib/modal';
import { AxiosError } from 'axios';
import ErrorMessage from '../ErrorMessage';
import ConfirmButtons from '@/components/ConfirmButtons';
import { expand } from 'inline-style-expand-shorthand';
import { GLOBAL_GRAPH_NAME, deleteGraph } from '@tigergraph/tools-models';
import { showToast } from '@/components/styledToasterContainer';
import { KIND } from 'baseui/toast';
import { ID_TOKEN_KEY, useWorkspaceContext } from '@/contexts/workspaceContext';
import { WorkspaceT } from '@/pages/workgroup/type';
import { axiosCluster } from '@/lib/network';

const graphStyleURL = '/api/graph-styles/local/[GRAPH_NAME]';
const loadingJobInfoURL = '/api/loading-job-info/[GRAPH_NAME]';
const queryDraftsURL = '/api/queries/[GRAPH_NAME]/drafts';
const explorationURL = '/api/exploration-results/[GRAPH_NAME]';

export interface DropGraphModalProps {
  isOpen: boolean;
  graphName: string;
  workspace: WorkspaceT;
  onClose: () => void;
  refetchSchema?: () => void;
}

export default function DropGraphModal(props: DropGraphModalProps) {
  const [css] = useStyletron();
  const { isOpen, graphName, workspace, onClose, refetchSchema } = props;
  const { graphNames, currentGraph, setCurrentGraph, setGraphNames } = useWorkspaceContext();
  const { userInfo, currentOrg } = useOrgContext();
  const [errorMessage, setErrorMessage] = useState<string | undefined>('');

  const dropGraphStyle = useMutation(['dropGraphStyle'], async (graphName: string) => {
    const response = await axiosCluster.delete(graphStyleURL.replace('[GRAPH_NAME]', graphName));
    return response.data;
  });

  const dropLoadingJobInfo = useMutation(['dropLoadingJobInfo'], async (graphName: string) => {
    const response = await axiosCluster.delete(loadingJobInfoURL.replace('[GRAPH_NAME]', graphName));
    return response.data;
  });

  const dropQueries = useMutation(['dropQueries'], async (graphName: string) => {
    const response = await axiosCluster.delete(queryDraftsURL.replace('[GRAPH_NAME]', graphName));
    return response.data;
  });

  const dropExploration = useMutation(['dropExploration'], async (graphName: string) => {
    const response = await axiosCluster.delete(explorationURL.replace('[GRAPH_NAME]', graphName));
    return response.data;
  });

  const { mutate: mutateDropGraph, isLoading } = useMutation(
    ['dropGraph'],
    async (graphName: string) => {
      const response = await deleteGraph(
        {
          graph: graphName,
        },
        {
          baseURL: `https://${workspace.nginx_host}`,
          version: workspace.tg_version,
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem(ID_TOKEN_KEY)}`,
          },
        }
      );
      return response;
    },
    {
      onSuccess: () => {
        trackGraphDeletedEvent(
          { deletion_trigger: 'direct', initiated_by_actor_type: 'user', graph_source: 'customer_created' },
          { userId: userInfo?.id, organizationId: currentOrg?.org_id, workspace, graphId: graphName }
        );
        handleClose();
        if (currentGraph === GLOBAL_GRAPH_NAME) {
          refetchSchema?.();
        }
        dropGraphStyle.mutate(graphName);
        dropLoadingJobInfo.mutate(graphName);
        dropQueries.mutate(graphName);
        dropExploration.mutate(graphName);
        if (currentGraph === graphName) {
          const newGraphName = graphNames.filter((name) => name !== GLOBAL_GRAPH_NAME && name !== graphName);
          if (newGraphName.length) {
            setCurrentGraph(newGraphName[0]);
          } else {
            setCurrentGraph(GLOBAL_GRAPH_NAME);
          }
        }
        setGraphNames(graphNames.filter((name) => name !== graphName));
        setErrorMessage(undefined);
        showToast({
          kind: KIND.positive,
          message: `Successfully dropped graph ${graphName}.`,
        });
      },
      onError: (error: AxiosError<{ message: string }>) => {
        setErrorMessage(error.response?.data?.message);
        showToast({
          kind: KIND.negative,
          message: `Failed to drop graph ${graphName}\n${error.response?.data?.message}`,
        });
      },
    }
  );

  const handleClose = () => {
    onClose();
    setErrorMessage(undefined);
  };

  return (
    <div
      className={css({
        display: 'flex',
        width: '100%',
      })}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          mutateDropGraph(graphName);
        }
      }}
    >
      <Modal
        onClose={handleClose}
        isOpen={isOpen}
        overrides={{
          Root: {
            style: {
              zIndex: 2,
            },
          },
        }}
      >
        <ModalHeader
          className={css({
            fontFamily: 'Urbanist',
            fontSize: '16px',
            fontWeight: 600,
            ...expand({
              padding: '0',
            }),
          })}
        >
          Delete Graph
        </ModalHeader>
        <ModalBody>
          <div
            className={css({
              display: 'flex',
              flexDirection: 'column',
              rowGap: '8px',
            })}
          >
            <div
              className={css({
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: 400,
              })}
            >
              Are you sure you want to drop graph {graphName}?
            </div>
            {errorMessage && <ErrorMessage message={errorMessage} />}
          </div>
        </ModalBody>
        <ModalFooter>
          <ConfirmButtons
            confirmLabel={'Confirm'}
            cancelLabel={'Cancel'}
            confirmLoading={isLoading}
            cancelDisabled={isLoading}
            onConfirm={() => {
              mutateDropGraph(graphName);
            }}
            onCancel={handleClose}
          />
        </ModalFooter>
      </Modal>
    </div>
  );
}
